import { EQuestionType } from '@hubertai/screening-question-types';
import clsx from 'clsx';
import { X } from 'lucide-react';
import type { ComponentProps } from 'react';
import type { TTextBubbleItem } from '.';
import HubertPopoverFieldEdit from 'components/HubertUI/HubertPopoverFieldEdit';
import {
  ECreateQuestionMode,
  useQuestionEditorContext,
} from 'components/Questionsv2/builder/context/create-question.context';
import UneditableInfoTooltip from 'components/Questionsv2/builder/UneditableInfoTooltip';
import { useTemplateId } from 'hooks/useTemplate';
import { editText, removeText } from 'rtk/Question.Editor.Slice';
import { customParametersUpdate } from 'rtk/TemplateSlice';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionMode,
  selectWritesTemplateOverrides,
} from 'rtk/questionEditorSelector';
import { EEventType } from 'types/events.enum';
import type { questionTextMessageType } from 'types/questions/question.types.v4';

interface Props extends ComponentProps<'div'> {
  data: {
    item: TTextBubbleItem;
    index: number;
    containerId: string;
  };
}

const TextBubblev2 = ({ data, ...props }: Props) => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const isPublishedOrNonExclusiveQuestion = useAppSelector(
    (state) => state.questionEditor.byId[questionId].isPublishedOrNonExclusiveQuestion,
  );
  const templateId = useTemplateId();
  const writesTemplateOverrides = useAppSelector((state) => selectWritesTemplateOverrides(state, questionId));
  const text = useAppSelector((state) => state.questionEditor.byId[questionId]);
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const disabled =
    isReadOnly ||
    (mode === ECreateQuestionMode.InTemplateEdit &&
      data.containerId === 'text' &&
      type !== EQuestionType.Info &&
      (isPublishedOrNonExclusiveQuestion ?? true));

  const textType = data.containerId as questionTextMessageType;
  const isDefault = text[textType].isDefault;
  const index = data.index;
  const language = questionLanguage;

  const update = (value: string) => {
    let s = value;
    if (s.endsWith('<p><br></p>')) {
      s = s.substring(0, s.length - 11);
    }
    console.log('new value ', data.containerId, s, index, questionLanguage);

    const x = [...text[textType].value];
    console.log('x', x);
    x[index] = { ...x[index], text: s };
    dispatch(
      editText({
        questionId,
        textType,
        value: x,
      }),
    );
    if (templateId && writesTemplateOverrides) {
      // add to parameters
      console.log('editTextAction x', x);

      const value = x.map((y) => {
        return y.text;
      });
      console.log('editTextAction x value', value);
      console.log('editTextAction x value', typeof value);
      console.log('editTextAction x value isArray', Array.isArray(value));

      dispatch(
        customParametersUpdate({
          templateId,
          resource: {
            nodeId: questionId,
            key: textType,
            value: x.map((y) => y.text),
            language,
            isDefault,
          },
          eventType: EEventType.ADDED,
          description: `User edited element belonging to ${questionId}/${textType}/${index} to "${s}".`,
        }),
      );
    }
  };

  if (disabled) {
    return (
      <UneditableInfoTooltip disabled={disabled}>
        <div
          className={clsx(
            'text-sm',
            'text-wrap',
            'p-3',
            'rounded-md',
            'bg-slate-50',
            'text-muted-foreground',
            'border border-slate-100',
          )}
          dangerouslySetInnerHTML={{
            __html: data.item.text || `Add ${data.containerId}`,
          }}
        />
      </UneditableInfoTooltip>
    );
  }

  return (
    <div
      {...props}
      className={clsx('flex grow justify-between gap-1', 'bg-white', 'px-3 py-2')}
      // dir={dir}
    >
      <HubertPopoverFieldEdit>
        <HubertPopoverFieldEdit.Trigger tooltipContent="edit this label" containerClass="w-full" asChild>
          <div
            className={clsx('text-sm', 'text-wrap', 'cursor-pointer', 'p-3', 'rounded-md', {
              'text-muted-foreground': !data.item.text,
            })}
            dangerouslySetInnerHTML={{
              __html: data.item.text || `Add ${data.containerId}`,
            }}
            dir={'auto'}
          />
        </HubertPopoverFieldEdit.Trigger>
        <HubertPopoverFieldEdit.Content
          editType="rich-text"
          value={data.item.text ?? undefined}
          onFieldCommit={(value: string) => {
            update(value);
          }}
        >
          <div>
            <div className="text-lg font-semibold">Edit question text</div>
          </div>
        </HubertPopoverFieldEdit.Content>
      </HubertPopoverFieldEdit>

      {!isReadOnly ? (
        <div
          className={clsx(
            'h-full',
            'hover:bg-gray-100',
            'hubert-vertical-center',
            'rounded-md',
            'w-12',
            'cursor-pointer',
          )}
          onClick={() => {
            const x = [...text[textType].value];
            x.splice(index, 1);
            dispatch(
              removeText({
                questionId,
                textType,
                value: x,
              }),
            );
            if (templateId && writesTemplateOverrides) {
              // update parameters
              dispatch(
                customParametersUpdate({
                  templateId,
                  resource: {
                    nodeId: questionId,
                    key: textType,
                    value: x.map((y) => y.text),
                    language,
                    isDefault,
                  },
                  eventType: EEventType.ADDED,
                  description: `User removed element index ${index} belonging to ${questionId}/${textType}.`,
                }),
              );
            }
          }}
        >
          <X className={clsx('m-auto text-gray-400')} />
        </div>
      ) : null}
    </div>
  );
};

export default TextBubblev2;
